import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../theme/ThemeContext';
import { fetchPatientHistory } from '../../services/api/visitApi';
import { fetchVisitMetadata, downloadFileSecure } from '../../services/api/visitMetadataApi';
import {
  groupVisitEntriesByAttendingDoctor,
  resolveSegmentDoctorName,
} from '../../utils/visitHistoryGrouping';
import { listSittingsForVisit } from '../../services/api/patientPlanApi';
import { listLabOrdersForVisit } from '../../services/api/labApi';
import { META_TYPES } from '../../constants/visitMetadata';
import { hasPrescriptionContent } from '../../utils/prescriptionContent';
import { hiddenVisitsLine, NO_VISITS_IN_YOUR_DEPARTMENTS_COPY } from '../../constants/admission';
import useFileViewerStore from '../../stores/useFileViewerStore';
import useSittingStore from '../../stores/sittingStore';
// NOTE (merge): origin/main also imported ProcedureNoteThreadPreview,
// LabReportActions and formatDateTimeIST here. This branch extracted those
// render sites into ./history/VisitProceduresHistorySection and
// ./history/VisitLabHistorySection, which import them directly — so the
// features are intact and these three imports are dead HERE (CI=true turns an
// unused import into a build error). SURGERY_SECTION_LABEL is still used below.
import { SURGERY_SECTION_LABEL } from '../../utils/rxMemory';
import { formatDoseSchedule } from '../../utils/rxDosage';
import syncEngine from '../../services/SyncEngine';
import { PhoneButton } from '../common/PhoneButton';
import { HistorySection } from './history/HistorySection';
import { EntryMeta } from './history/EntryMeta';
import { formatDateIST } from '../../utils/formatIST';
import { FileRow } from './history/FileRow';
import { VisitLabHistorySection } from './history/VisitLabHistorySection';
import { VisitProceduresHistorySection } from './history/VisitProceduresHistorySection';

/**
 * PatientHistory — Full-screen overlay showing a patient's visit history.
 *
 * Each visit is expandable showing:
 *  - Notes summary
 *  - Vitals
 *  - Symptoms
 *  - Prescriptions (viewable, separate section)
 *  - Reports (viewable, separate section)
 *
 * Summary and Prescription are shown in SEPARATE sections (not merged).
 */

const STATUS_COLORS = {
  QUEUE:       { bg: 'rgba(59,130,246,0.12)',  text: '#3b82f6' },
  IN_PROGRESS: { bg: 'rgba(13, 148, 136,0.12)',  text: '#0d9488' },
  COMPLETED:   { bg: 'rgba(34,197,94,0.12)',   text: '#22c55e' },
  CANCELLED:   { bg: 'rgba(107,114,128,0.12)', text: '#6b7280' },
};

export default function PatientHistory({ patientId, patientName, phoneNumber, onClose }) {
  const { isDark } = useTheme();
  const [visits, setVisits] = useState([]);
  const [outOfScopeCount, setOutOfScopeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedVisitId, setExpandedVisitId] = useState(null);
  const [visitMetadata, setVisitMetadata] = useState({}); // { visitId: entries[] }
  const [loadingMeta, setLoadingMeta] = useState({});
  // Phase 2.5 — sittings performed on a given visit. Keyed by visit_id
  // → SittingSummary[]. Fetched lazily on expand, same as metadata.
  const [visitSittings, setVisitSittings] = useState({});
  const [loadingSittings, setLoadingSittings] = useState({});
  // Lab orders (tests + reports) on a given visit. Keyed by visit_id →
  // LabOrder[]. Fetched lazily on expand, same as metadata/sittings.
  const [visitLabOrders, setVisitLabOrders] = useState({});
  const [loadingLab, setLoadingLab] = useState({});

  // Load patient history
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPatientHistory(patientId);
        if (!cancelled) {
          // Sort newest first. Same-day visits tie on visit_date, so break the
          // tie by a time field (created_at has seconds; visit_time is HH:MM)
          // descending — the latest / in-progress visit sorts above an earlier
          // completed one.
          const tkey = (v) => v.created_at || `${v.visit_date || ''}T${v.visit_time || ''}`;
          const sorted = (data.visits || []).slice().sort((a, b) => {
            const d = (b.visit_date || '').localeCompare(a.visit_date || '');
            return d !== 0 ? d : tkey(b).localeCompare(tkey(a));
          });
          setVisits(sorted);
          setOutOfScopeCount(data.out_of_scope_count || 0);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [patientId]);

  // Re-fetch a visit's lab orders (after a payment/report action on a row).
  const reloadLabOrders = useCallback(async (visitId) => {
    try {
      const r = await listLabOrdersForVisit(visitId);
      setVisitLabOrders((prev) => ({ ...prev, [visitId]: r.success ? (r.data || []) : [] }));
    } catch (err) {
      console.error('Failed to reload lab orders for visit:', visitId, err);
    }
  }, []);

  // Load metadata when expanding a visit — uses SyncEngine for dedup + caching
  const handleExpand = useCallback(async (visitId) => {
    if (expandedVisitId === visitId) {
      setExpandedVisitId(null);
      return;
    }
    setExpandedVisitId(visitId);

    const needMeta = !visitMetadata[visitId];
    const needSittings = !visitSittings[visitId];
    const needLab = !visitLabOrders[visitId];
    // Flip all loading flags up front so "No records" never flashes between the
    // sequential fetches below.
    if (needMeta) setLoadingMeta((prev) => ({ ...prev, [visitId]: true }));
    if (needSittings) setLoadingSittings((prev) => ({ ...prev, [visitId]: true }));
    if (needLab) setLoadingLab((prev) => ({ ...prev, [visitId]: true }));

    // Metadata (notes / vitals / symptoms / prescription / reports).
    if (needMeta) {
      try {
        const result = await syncEngine.fetchMetadata(
          visitId,
          async (vid) => fetchVisitMetadata(vid),
          { source: 'PatientHistory' }
        );
        setVisitMetadata((prev) => ({ ...prev, [visitId]: result?.data?.entries || [] }));
      } catch (err) {
        console.error('Failed to load metadata for visit:', visitId, err);
      } finally {
        setLoadingMeta((prev) => ({ ...prev, [visitId]: false }));
      }
    }

    // Phase 2.5 — procedures (sittings) on this visit (with their note threads).
    if (needSittings) {
      useSittingStore.getState().fetchVisitNotes(visitId);
      try {
        // include_historical so a procedure that has since MOVED off this
        // visit still shows here as a read-only "moved to [next visit]" trail.
        const r = await listSittingsForVisit(visitId, { includeHistorical: true });
        setVisitSittings((prev) => ({ ...prev, [visitId]: r.success ? (r.data?.sittings || []) : [] }));
      } catch (err) {
        console.error('Failed to load sittings for visit:', visitId, err);
        setVisitSittings((prev) => ({ ...prev, [visitId]: [] }));
      } finally {
        setLoadingSittings((prev) => ({ ...prev, [visitId]: false }));
      }
    }

    // Lab orders (tests + reports) on this visit — so a lab visit's completed
    // tests + their reports show in history instead of "No records".
    if (needLab) {
      try {
        const r = await listLabOrdersForVisit(visitId);
        setVisitLabOrders((prev) => ({ ...prev, [visitId]: r.success ? (r.data || []) : [] }));
      } catch (err) {
        console.error('Failed to load lab orders for visit:', visitId, err);
        setVisitLabOrders((prev) => ({ ...prev, [visitId]: [] }));
      } finally {
        setLoadingLab((prev) => ({ ...prev, [visitId]: false }));
      }
    }
  }, [expandedVisitId, visitMetadata, visitSittings, visitLabOrders]);

  const handleViewFile = useCallback(async (visitId, entry) => {
    try {
      const { blobUrl, contentType } = await downloadFileSecure(visitId, entry.meta_type, entry.meta_id);
      const ft = contentType?.startsWith('image') ? 'image' : 'pdf';
      useFileViewerStore.getState().openViewer({
        blobUrl,
        fileType: ft,
        title: entry.meta_type === 'PRESCRIPTION' ? 'Prescription' : entry.meta_type === 'REPORT' ? 'Report' : 'File',
      });
    } catch (err) {
      console.error('Failed to view file:', err);
    }
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // visit_date is a bare YYYY-MM-DD calendar day; pin it to IST midnight.
    return formatDateIST(`${dateStr}T00:00:00+05:30`) || dateStr;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const bg = isDark ? '#0f1117' : '#ffffff';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)';
  const border = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  return createPortal(
    <div style={{
      // Must render above ManageVisitPage's Smart View overlay (z-index
      // 9999). History is a modal ON TOP, not a sibling.
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      padding: '2rem 1rem',
      overflowY: 'auto',
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: '640px',
        background: bg,
        borderRadius: '1rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        animation: 'slideDown 0.25s ease',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${border}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        }}>
          <div>
            <h2 style={{
              margin: 0, fontSize: '1rem', fontWeight: 700,
              color: isDark ? '#f5f5f7' : '#111827',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              📋 Patient History
            </h2>
            <p style={{
              margin: '0.25rem 0 0', fontSize: '0.75rem',
              color: isDark ? '#9ca3af' : '#6b7280',
              display: 'flex', alignItems: 'center', gap: '0.375rem',
            }}>
              <span>{patientName || 'Patient'} — {visits.length} visit{visits.length !== 1 ? 's' : ''}</span>
              <PhoneButton phoneNumber={phoneNumber} isDark={isDark} size="sm" />
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              border: 'none', borderRadius: '0.5rem',
              width: '2rem', height: '2rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: '0.875rem',
              color: isDark ? '#9ca3af' : '#6b7280',
            }}
          >
            ✕
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
            <p style={{ fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
              Loading visit history...
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            margin: '1rem', padding: '0.75rem 1rem', borderRadius: '0.5rem',
            background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
            border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
            fontSize: '0.8125rem', color: isDark ? '#fca5a5' : '#991b1b',
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Visits hidden by the department wall (count only, no rows) */}
        {!loading && !error && outOfScopeCount > 0 && (
          <p
            data-testid="history-out-of-scope"
            style={{
              margin: '0.75rem 1rem 0', fontSize: '0.75rem',
              color: isDark ? '#9ca3af' : '#6b7280',
            }}
          >
            {hiddenVisitsLine(outOfScopeCount)}
          </p>
        )}

        {/* Empty */}
        {!loading && !error && visits.length === 0 && (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <span style={{ fontSize: '2.5rem' }}>🏥</span>
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', fontWeight: 500, color: isDark ? '#e8e9ed' : '#374151' }}>
              {outOfScopeCount > 0 ? NO_VISITS_IN_YOUR_DEPARTMENTS_COPY : 'No visit history found'}
            </p>
          </div>
        )}

        {/* Visit list */}
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {visits.map((visit, idx) => {
            const isExpanded = expandedVisitId === visit.visit_id;
            const entries = visitMetadata[visit.visit_id] || [];
            const isLoadingMeta = loadingMeta[visit.visit_id];
            const sittings = visitSittings[visit.visit_id] || [];
            const labOrders = visitLabOrders[visit.visit_id] || [];
            const isLoadingDetails = isLoadingMeta
              || loadingSittings[visit.visit_id] || loadingLab[visit.visit_id];

            const notes = entries.filter((e) => e.meta_type === META_TYPES.NOTE);
            const vitals = entries.filter((e) => e.meta_type === META_TYPES.VITAL);
            const symptoms = entries.filter((e) => e.meta_type === META_TYPES.SYMPTOM);
            const prescriptions = entries.filter((e) => e.meta_type === META_TYPES.PRESCRIPTION);
            const reports = entries.filter((e) => e.meta_type === META_TYPES.REPORT);

            return (
              <div key={visit.visit_id} style={{
                borderTop: idx > 0 ? `1px solid ${border}` : 'none',
              }}>
                {/* Visit card header */}
                <button
                  onClick={() => handleExpand(visit.visit_id)}
                  style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.875rem 1.25rem',
                    background: isExpanded ? cardBg : 'transparent',
                    border: 'none', cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.8125rem', fontWeight: 600,
                        color: isDark ? '#f5f5f7' : '#111827',
                      }}>
                        {formatDate(visit.visit_date)}
                      </span>
                      {visit.visit_id && (
                        <span style={{
                          fontSize: '0.5625rem', fontWeight: 600, letterSpacing: '0.03em',
                          padding: '0.1rem 0.45rem', borderRadius: '0.375rem',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                          color: isDark ? '#cbd5e1' : '#475569',
                        }}>
                          {String(visit.visit_id).slice(-6)}
                        </span>
                      )}
                      <span style={{
                        padding: '0.1rem 0.4rem', borderRadius: '9999px',
                        fontSize: '0.5625rem', fontWeight: 600,
                        textTransform: 'uppercase',
                        background: (STATUS_COLORS[visit.status] || STATUS_COLORS.QUEUE).bg,
                        color: (STATUS_COLORS[visit.status] || STATUS_COLORS.QUEUE).text,
                      }}>
                        {visit.status}
                      </span>
                      {visit.visit_type && visit.visit_type !== 'REGULAR' && (
                        <span style={{
                          padding: '0.1rem 0.4rem', borderRadius: '9999px',
                          fontSize: '0.5625rem', fontWeight: 500,
                          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          color: isDark ? '#9ca3af' : '#6b7280',
                        }}>
                          {visit.visit_type}
                        </span>
                      )}
                    </div>
                    {visit.doctor_name && (
                      <div style={{
                        fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280',
                        marginTop: '0.25rem',
                      }}>
                        🩺 Dr. {visit.doctor_name}
                      </div>
                    )}
                    {/* Phase 3.1 — multi-doctor referral chain badge */}
                    {(() => {
                      const { isMultiDoctor, segments } =
                        groupVisitEntriesByAttendingDoctor(visit);
                      if (!isMultiDoctor) return null;
                      const labels = segments.map((seg) =>
                        resolveSegmentDoctorName(seg, visit, entries)
                      );
                      return (
                        <div style={{
                          marginTop: '0.25rem',
                          display: 'inline-flex', alignItems: 'center',
                          gap: '0.25rem', flexWrap: 'wrap',
                          padding: '0.15rem 0.5rem', borderRadius: '9999px',
                          fontSize: '0.5625rem', fontWeight: 600,
                          background: isDark ? 'rgba(249,115,22,0.15)' : '#fff7ed',
                          border: `1px solid ${isDark ? 'rgba(249,115,22,0.4)' : '#fdba74'}`,
                          color: isDark ? '#fed7aa' : '#9a3412',
                        }}>
                          <span>🟠</span>
                          {labels.map((name, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <span>→</span>}
                              <span>{name}</span>
                            </React.Fragment>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <span style={{
                    fontSize: '0.75rem',
                    color: isDark ? '#6b7280' : '#9ca3af',
                    transform: isExpanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}>
                    ▼
                  </span>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{
                    padding: '0 1.25rem 1rem',
                    background: cardBg,
                  }}>
                    {isLoadingMeta && (
                      <p style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af', textAlign: 'center', padding: '0.5rem 0' }}>
                        Loading details...
                      </p>
                    )}

                    {!isLoadingDetails && entries.length === 0 && sittings.length === 0 && labOrders.length === 0 && (
                      <p style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af', textAlign: 'center', padding: '0.5rem 0' }}>
                        No records for this visit.
                      </p>
                    )}

                    {/* ── Notes Section ── */}
                    {notes.length > 0 && (
                      <HistorySection title="📝 Notes" isDark={isDark}>
                        {notes.map((n) => (
                          <div key={n.meta_id} style={{
                            padding: '0.375rem 0.5rem', borderRadius: '0.375rem',
                            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                            marginBottom: '0.25rem',
                          }}>
                            <p style={{ margin: 0, fontSize: '0.6875rem', color: isDark ? '#e8e9ed' : '#374151', lineHeight: 1.5 }}>
                              {n.content}
                            </p>
                            <EntryMeta entry={n} isDark={isDark} />
                          </div>
                        ))}
                      </HistorySection>
                    )}

                    {/* ── Vitals Section ── */}
                    {vitals.length > 0 && (
                      <HistorySection title="🌡️ Vitals" isDark={isDark}>
                        {vitals.map((v) => (
                          <div key={v.meta_id} style={{ marginBottom: '0.375rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                              {(v.vitals || []).map((vt, i) => (
                                <span key={i} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                                  padding: '0.15rem 0.4rem', borderRadius: '0.375rem',
                                  fontSize: '0.5625rem',
                                  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                                  color: isDark ? '#e8e9ed' : '#374151',
                                }}>
                                  <span style={{ fontWeight: 500, color: isDark ? '#9ca3af' : '#6b7280' }}>{vt.label}:</span>
                                  <span style={{ fontWeight: 600 }}>{vt.value}</span>
                                  <span style={{ color: isDark ? '#6b7280' : '#9ca3af' }}>{vt.unit}</span>
                                </span>
                              ))}
                            </div>
                            <EntryMeta entry={v} isDark={isDark} />
                          </div>
                        ))}
                      </HistorySection>
                    )}

                    {/* ── Symptoms Section ── */}
                    {symptoms.length > 0 && (
                      <HistorySection title="🤒 Symptoms" isDark={isDark}>
                        {symptoms.map((s) => (
                          <div key={s.meta_id} style={{ marginBottom: '0.375rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
                              {(s.symptoms || []).map((sym, i) => (
                                <span key={i} style={{
                                  padding: '0.1rem 0.4rem', borderRadius: '9999px',
                                  fontSize: '0.5625rem', fontWeight: 500,
                                  background: isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)',
                                  color: '#3b82f6',
                                }}>
                                  {sym.name}
                                </span>
                              ))}
                            </div>
                            <EntryMeta entry={s} isDark={isDark} />
                          </div>
                        ))}
                      </HistorySection>
                    )}

                    {/* ── Prescriptions Section (SEPARATE) ── */}
                    {prescriptions.length > 0 && (
                      <HistorySection title="💊 Prescriptions" isDark={isDark}>
                        {prescriptions.map((p) => {
                          // ── Digital prescription → show its details ──
                          // Content rule is the ONE shared `hasPrescriptionContent`
                          // — a DISPLAY question, not a copy of a backend rule — NOT
                          // "has medicines" (bk-1150): a diagnosis-only / tests-only
                          // prescription used to fall through to the uploaded-file
                          // branch below and render a phantom "📄 File · 👁 View"
                          // row with no file behind it.
                          const medicines = Array.isArray(p.medicines) ? p.medicines : [];
                          const diagnosis = (p.diagnosis || '').trim();
                          if (p.prescription_type === 'digital' && hasPrescriptionContent(p)) {
                            return (
                              <div key={p.meta_id} style={{
                                borderRadius: '0.5rem',
                                border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.12)' : 'rgba(13, 148, 136,0.08)'}`,
                                background: isDark ? 'rgba(13, 148, 136,0.04)' : 'rgba(13, 148, 136,0.02)',
                                marginBottom: '0.375rem', overflow: 'hidden',
                              }}>
                                {/* Header */}
                                <div style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '0.375rem 0.5rem',
                                  borderBottom: `1px solid ${isDark ? 'rgba(13, 148, 136,0.08)' : 'rgba(13, 148, 136,0.05)'}`,
                                }}>
                                  <span style={{ fontSize: '0.625rem', fontWeight: 600, color: isDark ? '#5eead4' : '#0f766e' }}>
                                    {medicines.length > 0
                                      ? `💊 ${medicines.length} medicine${medicines.length !== 1 ? 's' : ''}`
                                      : '💊 Prescription'}
                                  </span>
                                  <span style={{ fontSize: '0.5rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                                    {p.added_by_name} • Digital
                                  </span>
                                </div>

                                {/* Diagnosis — content in its own right (bk-1150).
                                    Same field `PrescriptionReadOnlyView` already
                                    renders on the patient card / visit panel; this
                                    surface has its own inline renderer, so the line
                                    is added here to match. */}
                                {diagnosis && (
                                  <div style={{
                                    padding: '0.25rem 0.5rem 0.375rem',
                                    fontSize: '0.625rem',
                                    color: isDark ? '#e8e9ed' : '#1f2937',
                                  }}>
                                    <span style={{ fontWeight: 700, color: isDark ? '#9ca3af' : '#6b7280' }}>Diagnosis: </span>
                                    {diagnosis}
                                  </div>
                                )}

                                {/* Medicine list */}
                                {medicines.length > 0 && (
                                <div style={{ padding: '0.25rem 0.5rem 0.375rem' }}>
                                  {/* `medicines`, NOT `p.medicines` — the local is
                                      the `|| []` coercion of a legacy row whose
                                      `medicines` arrives as JSON `null` (bk-1182);
                                      mapping the raw field white-screens the panel.
                                      `formatDoseSchedule` is master's half-dose
                                      spelling (½), which the hand-built
                                      `${morning}-${afternoon}-${night}` template
                                      here could not produce. */}
                                  {medicines.map((med, idx) => {
                                    const dosageStr = formatDoseSchedule(med.dosage_schedule);
                                    return (
                                      <div key={idx} style={{
                                        display: 'flex', alignItems: 'baseline', gap: '0.375rem', flexWrap: 'wrap',
                                        padding: '0.1875rem 0',
                                        borderBottom: idx < medicines.length - 1
                                          ? `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}` : 'none',
                                      }}>
                                        <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#e8e9ed' : '#1f2937' }}>
                                          {med.medicine}
                                        </span>
                                        {med.strength && (
                                          <span style={{ fontSize: '0.5625rem', fontWeight: 500, color: '#0d9488' }}>
                                            {med.strength}
                                          </span>
                                        )}
                                        {med.form && (
                                          <span style={{ fontSize: '0.5625rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                                            {med.form}
                                          </span>
                                        )}
                                        {dosageStr && (
                                          <span style={{
                                            fontSize: '0.5625rem', fontWeight: 600, color: '#3b82f6',
                                            padding: '0.0625rem 0.25rem', borderRadius: '0.25rem',
                                            background: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.06)',
                                          }}>
                                            {dosageStr}
                                          </span>
                                        )}
                                        {med.timing && (
                                          <span style={{ fontSize: '0.5rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                                            {med.timing}
                                          </span>
                                        )}
                                        {med.duration && (
                                          <span style={{ fontSize: '0.5rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                                            {med.duration}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                )}

                                {/* Doctor notes */}
                                {p.doctor_notes && (
                                  <div style={{
                                    padding: '0.25rem 0.5rem 0.375rem',
                                    borderTop: `1px solid ${isDark ? 'rgba(13, 148, 136,0.08)' : 'rgba(13, 148, 136,0.05)'}`,
                                    fontSize: '0.5625rem', fontStyle: 'italic',
                                    color: isDark ? '#9ca3af' : '#6b7280',
                                  }}>
                                    📝 {p.doctor_notes}
                                  </div>
                                )}

                                {/* Investigations */}
                                {p.investigations && p.investigations.length > 0 && (
                                  <div style={{
                                    padding: '0.25rem 0.5rem 0.375rem',
                                    borderTop: `1px solid ${isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)'}`,
                                    display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center',
                                  }}>
                                    <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: isDark ? '#fbbf24' : '#d97706' }}>🧪</span>
                                    {p.investigations.map((inv, i) => (
                                      <span key={i} style={{
                                        fontSize: '0.5rem', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem',
                                        background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)',
                                        color: isDark ? '#fbbf24' : '#d97706', fontWeight: 500,
                                      }}>{inv}</span>
                                    ))}
                                  </div>
                                )}

                                {/* Advised surgeries — G2 `advised_surgeries: string[] | null`;
                                    absent/null/[] renders nothing (no heading, no blank band). */}
                                {p.advised_surgeries && p.advised_surgeries.length > 0 && (
                                  <div style={{
                                    padding: '0.25rem 0.5rem 0.375rem',
                                    borderTop: `1px solid ${isDark ? 'rgba(124,58,237,0.12)' : 'rgba(124,58,237,0.08)'}`,
                                    display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center',
                                  }}>
                                    <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: isDark ? '#c4b5fd' : '#5b21b6' }}>
                                      {`🔪 ${SURGERY_SECTION_LABEL}`}
                                    </span>
                                    {p.advised_surgeries.map((s, i) => (
                                      <span key={i} style={{
                                        fontSize: '0.5rem', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem',
                                        background: isDark ? 'rgba(124,58,237,0.2)' : '#ede9fe',
                                        color: isDark ? '#c4b5fd' : '#5b21b6', fontWeight: 500,
                                      }}>{s}</span>
                                    ))}
                                  </div>
                                )}

                                {/* Follow-up is single-sourced on the visit (shown via the
                                    visit's "Follow-up" label) — not per-prescription anymore. */}
                              </div>
                            );
                          }

                          // ── Uploaded file prescription → show file with View button ──
                          return (
                            <FileRow
                              key={p.meta_id}
                              entry={p}
                              isDark={isDark}
                              formatSize={formatSize}
                              onView={() => handleViewFile(visit.visit_id, p)}
                            />
                          );
                        })}
                      </HistorySection>
                    )}

                    {/* ── Procedures Section (Phase 2.5) ── */}
                    <VisitProceduresHistorySection
                      sittings={visitSittings[visit.visit_id] || []}
                      loading={loadingSittings[visit.visit_id]}
                      isDark={isDark}
                      visitId={visit.visit_id}
                      visitDate={visit.visit_date}
                    />

                    {/* ── Lab tests Section — the visit's lab orders + reports ── */}
                    <VisitLabHistorySection
                      orders={labOrders}
                      loading={loadingLab[visit.visit_id]}
                      isDark={isDark}
                      onChanged={() => reloadLabOrders(visit.visit_id)}
                    />

                    {/* ── Reports Section (SEPARATE) ── */}
                    {reports.length > 0 && (
                      <HistorySection title="📄 Reports" isDark={isDark}>
                        {reports.map((r) => (
                          <FileRow
                            key={r.meta_id}
                            entry={r}
                            isDark={isDark}
                            formatSize={formatSize}
                            onView={() => handleViewFile(visit.visit_id, r)}
                          />
                        ))}
                      </HistorySection>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

